/**
 * Request shape + wire serialisation for the QV `/ws/qv` endpoint.
 *
 * Transport exposes a camelCase {@link IndicatorRequest}; the QV backend expects
 * a PascalCase JSON object, gzip-compressed, delivered as one binary frame
 * (`json.loads(gzip.decompress(raw))` in `ws_ind.ws_indicators`).
 */

import type { Indicator } from "@qufolio/model";

/** One chart group in a request: a symbol/frequency plus the indicators to compute. */
export interface ChartGroupRequest {
  /** QV `ChartGroupId` — a uint32-range integer (as string or number). */
  chartGroupId: string | number;

  /** Symbol expression, e.g. `"AAPL"` or a ratio like `"AAPL/SPY"`. */
  symbol: string;

  /** Bar frequency / timeframe, e.g. `"D"`, `"60"`, `"5"`. */
  frequency: string;

  /**
   * How many bars of history to return — the contract's `ChartGroup.barsRange`.
   *
   * Omitted means the server's default. Sent as `barsRange`, the contract's
   * spelling: the QV envelope this format came from says `BarsRange` and the
   * server accepts either, so there is no reason for a second spelling to leave
   * here.
   */
  barsRange?: number;

  /**
   * How a bar is **formed** — the contract's `ChartGroup.barSource`.
   *
   * `heikinAshi` re-forms every bar before the server computes a single
   * indicator, so the same symbol at the same frequency is a different table.
   * Omitted means ordinary time bars.
   *
   * It has to be listed here and in `serializeRequest` below, because that
   * function names every field it forwards. A field added to the contract and
   * not to both is dropped **silently** on the way to the wire: the chart still
   * renders, and its overlays are simply computed from the wrong bars.
   */
  barSource?: string;

  /**
   * Continue the indicators past the last bar — the contract's
   * `ChartGroup.projectForward`.
   *
   * It decides whether the server builds projected bars at all, so the `~fwd`
   * aliases in {@link indicators} are answered or silently omitted. **The
   * aliases are not the request**: they say *which* continuations, and this says
   * whether there are any.
   *
   * Third field to be added to this interface, and the third to be forgotten
   * here first — `barsRange` and `barSource` both shipped with the app sending
   * them and this dropping them. The serialiser below rebuilds the wire object
   * from a whitelist, so a field the app adds and this does not is discarded
   * between two halves that each look correct. There is now a test per field.
   */
  projectForward?: boolean;

  /**
   * A TopDown index's members (`docs/features/topdown.md`), sent beside a
   * `~TDe.<hash>` symbol. Not a contract field: the symbol names the index by
   * a hash of this list, and the server checks the list against it. Absent for
   * every other symbol.
   */
  indexMembers?: string[];

  /** Indicator specs to compute for this group (contract {@link Indicator} type). */
  indicators: Indicator[];
}

/** A full indicator request for one layout across many chart groups. */
export interface IndicatorRequest {
  /** Workspace / layout id echoed back in the bundle header. */
  layoutId: string;

  /** Authenticated user id (QV `user_id`). */
  userId: string | number;

  /** Application id (QV `app_id`); defaults to `2` on the wire when omitted. */
  appId?: string | number;

  /** Chart groups to compute. */
  chartGroups: ChartGroupRequest[];
}

/** The PascalCase JSON object QV's WebSocket handler parses. */
export interface QvWireRequest {
  LayoutId: string;
  user_id: string | number;
  app_id: string | number;
  ChartGroups: Array<{
    ChartGroupId: string | number;
    symbol: string;
    frequency: string;
    barsRange?: number;
    barSource?: string;
    projectForward?: boolean;
    indexMembers?: string[];
    indicators: unknown[];
  }>;
}

/**
 * Map QV's PascalCase indicator field names from a contract {@link Indicator}.
 * QV's `ta.Indicator` (pydantic) keys columns by `Aliases`, so the alias list is
 * carried through verbatim; unknown/optional fields are dropped when absent.
 */
function toWireIndicator(ind: Indicator): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    Shortname: ind.shortname,
    Name: ind.name,
    Category: ind.category ?? "custom",
    PlotType: ind.plotType ?? "line",
    Inputs: (ind.inputs ?? []).map((i) => ({ ColumnName: i.default ?? i.name })),
    Params: (ind.params ?? []).map((p) => ({
      Name: p.name,
      Value: String(p.default),
    })),
    OverColumns: ind.overColumns ?? [],
    Aliases: ind.aliases ?? [],
  };
  if (ind.formula != null) wire["Formula"] = ind.formula;
  if (ind.roleId != null) wire["RoleId"] = ind.roleId;
  return wire;
}

/**
 * Convert a camelCase {@link IndicatorRequest} into the exact PascalCase JSON
 * object QV expects. Pure — no compression or I/O.
 */
export function serializeRequest(req: IndicatorRequest): QvWireRequest {
  return {
    LayoutId: req.layoutId,
    user_id: req.userId,
    app_id: req.appId ?? 2,
    ChartGroups: req.chartGroups.map((cg) => ({
      ChartGroupId: cg.chartGroupId,
      symbol: cg.symbol,
      frequency: cg.frequency,
      // Omitted rather than sent as null when absent: the server reads a missing
      // key as "your default", and an explicit null would have to be special-cased
      // there to mean the same thing.
      ...(cg.barsRange !== undefined ? { barsRange: cg.barsRange } : {}),
      ...(cg.barSource !== undefined ? { barSource: cg.barSource } : {}),
      ...(cg.projectForward !== undefined ? { projectForward: cg.projectForward } : {}),
      ...(cg.indexMembers !== undefined ? { indexMembers: cg.indexMembers } : {}),
      indicators: cg.indicators.map(toWireIndicator),
    })),
  };
}

/**
 * Encode a request to the binary frame QV reads: gzip-compressed UTF-8 JSON.
 *
 * Uses the platform `CompressionStream` (browsers + Node 18+). When gzip is
 * unavailable it returns the uncompressed JSON bytes; a custom `compress` hook
 * can be injected (e.g. for tests or a non-gzip backend variant).
 *
 * @param req      - The request to encode.
 * @param compress - Optional override for the gzip step.
 */
export async function encodeRequest(
  req: IndicatorRequest,
  compress: (bytes: Uint8Array) => Promise<Uint8Array> | Uint8Array = gzip,
): Promise<Uint8Array> {
  const json = JSON.stringify(serializeRequest(req));
  const bytes = new TextEncoder().encode(json);
  return compress(bytes);
}

/** Gzip via the platform `CompressionStream`; identity fallback when absent. */
export async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  const CS = (globalThis as { CompressionStream?: unknown }).CompressionStream;
  if (typeof CS !== "function") return bytes;
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new (CS as typeof CompressionStream)("gzip"));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}
